import { createActions } from 'redux-actions';
import analytics from '../../shared/services/analytics/analytics';
import * as wizardSelectors from '../../reducers/AccountReducer/AccountWizardSelector';
import { protoUserListSelector, stageFormSelector, stageSelector } from '../../reducers/AccountReducer/AccountWizardSelector';
import { StateType } from '../../reducers';
import { accountTermsAccepted, fetchCreateAccount, responseAccountFailure } from './AccountActions';
import { competitionSelector, logoutCompetitionIdSelector } from '../../reducers/CompetitionsReducer/CompetitionsSelector';
import { prepareStructureForSubmit, queryWizardField } from '../../containers/Account/wizard/formHelpers';
import * as Fields from '../../components/Account/Data/Fields';
import { setDataToSubmit } from '../../components/Account/Create/helpers';
import { isAction, webSimApiErrorFlashMessages } from '../../shared/helpers/fetch-helper';
import { createAccountfetch } from './Helper';
import { userAccountStructure } from '../../shared/data/User';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { logErrorService } from '../../shared/services';
import { setAgreementOpen } from '../AgreementsActions';
import { USER_AGREEMENTS_ID } from '../../components/Agreements/UserAgreements';
import { FieldErrors } from 'react-hook-form';
import { AppDispatch } from '../../shared/services/store';
import { CAPTCHA_RETRY_SYMBOL, captchaExecuteAsync } from '../../reducers/CaptchaReducer/CaptchaActions';
import { fetchCreateProtoUser } from '../../shared/api/api';

export const {
  setFormData,
  setStage: setStageAction,
  changeFormField: changeFormFieldAction,
  setSubmittedStages,
  setInitComplete,
  setStageForm,
  nextStage: nextStageAction,
  prevStage: prevStageAction,
  setError,
  setCurrentStageSubmitted: setCurrentStageSubmittedAction,
  setErrorPopup,
  resetWizardData,
  setAgreementsFormStage,
  addProtoUser,
} = createActions({
  SET_FORM_DATA: data => data,
  CHANGE_FORM_FIELD: data => data,
  SET_STAGE: data => data,
  SET_SUBMITTED_STAGES: data => data,
  SET_STAGE_FORM: data => data,
  SET_ERROR: data => data,
  SET_INIT_COMPLETE: data => data,
  SET_CURRENT_STAGE_SUBMITTED: data => data,
  SET_ERROR_POPUP: data => data,
  RESET_WIZARD_DATA: data => data,
  SET_AGREEMENTS_FORM_STAGE: data => data,
  ADD_PROTO_USER: data => data,
});

export const setPasswordLikeNameError = () => setError({ name: 'passwordLikeName', value: true });

export const showErrorPopup =
  ({ targetId, content }: { targetId: string; content: string }) =>
  (dispatch: AppDispatch, getState: () => StateType) => {
    dispatch(setErrorPopup({ targetId, content }));
  };

export const setCurrentStageSubmitted = () => (dispatch: AppDispatch, getState: () => StateType) => {
  const currentStage = stageSelector(getState());
  dispatch(setCurrentStageSubmittedAction());

  analytics.trackCustomEventOnce(`wizard_sign_up_stage_submitted`, { event_label: String(currentStage) });
};
export const highlightFirstError = (errors: object) => (dispatch: AppDispatch) => {
  const firstErrorKey = Object.keys(errors)[0];
  const fieldElement = queryWizardField(firstErrorKey);
  if (fieldElement) {
    fieldElement.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }
  dispatch(showErrorPopup({ targetId: firstErrorKey, content: 'Please correct the errors before continue' }));
};

// This function validates the form both on the client and the server side.
export const validateLocalAndServer = () => async (dispatch: AppDispatch, getState: () => StateType) => {
  let errors;
  const stageForm = wizardSelectors.stageFormSelector(getState());

  // First, it validates the form on the client side.
  await stageForm?.handleSubmit(
    () => null,
    stageErrors => (errors = stageErrors)
  )();

  if (errors) {
    dispatch(highlightFirstError(errors));
    return errors;
  }

  // If there are no client-side errors, it validates the form on the server side.
  const serverErrors = await dispatch(validateOnServer());

  if (serverErrors) {
    dispatch(highlightFirstError(serverErrors));
    return serverErrors;
  }
};
export const backAction = () => async (dispatch: AppDispatch, getState: () => StateType) => {
  const isCurrentStageSubmitted = wizardSelectors.currentStageSubmittedSelector(getState());

  if (isCurrentStageSubmitted) {
    const errors = await dispatch(validateLocalAndServer());

    if (errors) {
      return;
    }
  }
  dispatch(prevStage());
};

// This function validates the form on the server side.
export const validateOnServer = () => async (dispatch: AppDispatch, getState: () => StateType) => {
  try {
    const state = getState();

    let serverValidation = null;
    const stageForm = wizardSelectors.stageFormSelector(state);
    let stageErrors = {} as Record<string, string>;

    const mountFieldNames = wizardSelectors.mountFieldNamesSelector(state);
    const dataFields = wizardSelectors.cleanedDataFieldsSelector(state);

    const competitionExists = competitionSelector(state) as boolean;
    const competitionId = logoutCompetitionIdSelector(state) as string | undefined;
    const dataFieldsForSubmit = setDataToSubmit({
      dataFields: prepareStructureForSubmit(dataFields),
      competitionId,
      competitionExists,
    });

    // delete some fields not to submit the form accidentally
    delete dataFieldsForSubmit.auxiliary.captcha;
    delete dataFieldsForSubmit.auxiliary.agree;

    // It sends a request to the server to validate the form.

    try {
      serverValidation = await createAccountfetch(dataFieldsForSubmit).then(res => (res as any).json());
    } catch (e) {
      console.error(e);
    }

    // It checks the server response for any errors.
    for (const key of mountFieldNames) {
      const modelPath = (userAccountStructure as any)[key]?.join('.');
      if (modelPath) {
        const error = get(serverValidation, modelPath)?.[0];

        if (error) {
          stageErrors[key] = error;
        }
      }
    }

    // If there are any server-side errors, it sets them in the form.
    if (!isEmpty(stageErrors)) {
      for (const key in stageErrors) {
        stageForm.setError(key, { type: 'validate', message: stageErrors[key] });
      }
      return stageErrors;
    }
  } catch (e) {
    logErrorService(e);
    webSimApiErrorFlashMessages(dispatch);
  }
};

export const createProtoUser = () => async (dispatch: AppDispatch, getState: () => StateType) => {
  try {
    const dataFields = wizardSelectors.cleanedDataFieldsSelector(getState());
    const captchaToken = await dispatch(captchaExecuteAsync());
    if (captchaToken === CAPTCHA_RETRY_SYMBOL) return;
    const email = dataFields.get(Fields.email.props.name);

    if (!protoUserListSelector(getState()).includes(email)) {
      fetchCreateProtoUser({
        agreePrivacyPolicy: dataFields.get(Fields.privacyPolicy.props.name),
        email: dataFields.get(Fields.email.props.name),
        captcha: captchaToken,
      });

      dispatch(addProtoUser(email));
    }
  } catch (e) {
    logErrorService(e);
    webSimApiErrorFlashMessages(dispatch);
  }
};

export const next = () => async (dispatch: AppDispatch, getState: () => StateType) => {
  try {
    const state = getState();

    if (wizardSelectors.stageSelector(state) === 0) {
      dispatch(createProtoUser());
    }

    // validate on server before going next, client validation is done by this point
    const serverErrors = await dispatch(validateOnServer());
    if (serverErrors) {
      dispatch(highlightFirstError(serverErrors));
      return;
    }

    dispatch(setCurrentStageSubmitted());
    const isLastStage = wizardSelectors.isLastStageSelector(state);

    // if this is last stage, open agreement modal
    if (isLastStage) {
      dispatch(setAgreementOpen({ id: USER_AGREEMENTS_ID }));
    } else {
      dispatch(nextStage());
    }
  } catch (e) {
    logErrorService(e);
    webSimApiErrorFlashMessages(dispatch);
  }
};

export const onError = (errors: FieldErrors<any>) => (dispatch: AppDispatch, getState: () => StateType) => {
  try {
    dispatch(highlightFirstError(errors));
  } catch (e) {
    logErrorService(e);
    webSimApiErrorFlashMessages(dispatch);
  }
};

export const handleAcceptedAction = (data: any) => async (dispatch: AppDispatch, getState: () => StateType) => {
  try {
    const captchaToken = await dispatch(captchaExecuteAsync());

    if (captchaToken === CAPTCHA_RETRY_SYMBOL) {
      return CAPTCHA_RETRY_SYMBOL;
    }

    dispatch(accountTermsAccepted(true));

    const state = getState();

    const competitionExists = competitionSelector(state) as boolean;

    const competitionId = logoutCompetitionIdSelector(state) as string | undefined;

    const formData = wizardSelectors.cleanedDataFieldsSelector(state);

    let immutableDataFields = prepareStructureForSubmit(formData).setIn(['agree', 'value'], data[Fields.agreeTermsConditions.props.name]);

    if (captchaToken) {
      immutableDataFields = immutableDataFields.setIn([Fields.captcha.props.name, 'value'], captchaToken);
    }

    // pick competition field and captcha field from agreement form
    if (data[Fields.agreeCompetition.props.name] !== undefined) {
      immutableDataFields = immutableDataFields.setIn(['agreeCompetition', 'value'], data[Fields.agreeCompetition.props.name]);
    }

    const submitData = setDataToSubmit({ dataFields: immutableDataFields, competitionId, competitionExists });
    const actionResult = await dispatch(fetchCreateAccount(submitData));
    if (!isAction(actionResult, responseAccountFailure().type)) {
      dispatch(resetWizardData());
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : JSON.stringify(error);
    logErrorService(errorMsg);
    // Throw the error to outside, this will make sure no change to current behaviour
    throw error;
  }
};

export const onSubmit = () => async (dispatch: AppDispatch, getState: () => StateType) => {
  try {
    const state = getState();
    const stageForm = stageFormSelector(state);

    return stageForm.handleSubmit(
      () => dispatch(next()),
      errors => dispatch(onError(errors))
    )();
  } catch (e) {
    logErrorService(e);
    webSimApiErrorFlashMessages(dispatch);
  }
};

export const setStage = (stage: number) => (dispatch: AppDispatch, getState: () => StateType) => {
  dispatch(setStageAction(stage));
};

export const prevStage = () => (dispatch: AppDispatch, getState: () => StateType) => {
  const stage = stageSelector(getState());

  dispatch(setStage(stage - 1));
  analytics.trackCustomEvent('wizard_sign_up_prev_stage');
};

export const nextStage = () => (dispatch: AppDispatch, getState: () => StateType) => {
  const stage = stageSelector(getState());

  dispatch(setStage(stage + 1));
  analytics.trackCustomEvent('wizard_sign_up_next_stage');
};

export const changeFormField = (payload: { name: string; value: any }) => (dispatch: AppDispatch, getState: () => StateType) => {
  dispatch(changeFormFieldAction(payload));
};
